import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Clock, Calendar, Users, Play, Check, Loader2,
  RotateCcw, Settings, Plus, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, MessageSquare, AlertCircle,
  Search, User, Phone,
} from 'lucide-react';
import InlineEditField from './InlineEditField';
import leadService from '../../services/global/leadService';

const StatusBadge = ({ status, colors }) => {
  const config = {
    draft: { label: 'Rascunho', bg: `${colors.textSecondary}20`, color: colors.textSecondary },
    active: { label: 'Ativo', bg: '#22c55e20', color: '#22c55e' },
    paused: { label: 'Pausado', bg: '#f59e0b20', color: '#f59e0b' },
  };
  const c = config[status] || config.draft;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 600, backgroundColor: c.bg, color: c.color,
    }}>
      {c.label}
    </span>
  );
};

const DAYS_PER_PAGE = 30;
const MAX_DAYS = 90;

// ============================================================
// Lead Selector Component
// ============================================================
const LeadSelector = ({ colors, styles, onSelect, selectedLead }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const loadLeads = async () => {
      setLoading(true);
      try {
        const data = await leadService.list();
        // API returns { data: [...] }
        const list = Array.isArray(data) ? data : (data.data || data.leads || []);
        setLeads(list);
      } catch (err) {
        console.error('Erro ao carregar leads:', err);
      } finally {
        setLoading(false);
      }
    };
    loadLeads();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = leads.filter(lead => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = (lead.name || '').toLowerCase();
    const phone = (lead.phone || '').toLowerCase();
    return name.includes(term) || phone.includes(term);
  });

  return (
    <div ref={dropdownRef} style={{ position: 'relative', flex: 1 }}>
      {/* Selected lead display / trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          ...styles.input,
          display: 'flex', alignItems: 'center', gap: '8px',
          cursor: 'pointer', minHeight: '40px', padding: '8px 12px',
          backgroundColor: selectedLead ? `${colors.accentPrimary}08` : colors.bgPrimary,
          border: `1px solid ${selectedLead ? colors.accentPrimary : colors.borderColor}`,
        }}
      >
        {selectedLead ? (
          <>
            <User size={14} color={colors.accentPrimary} />
            <span style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary, flex: 1 }}>
              {selectedLead.name || 'Sem nome'}
            </span>
            <span style={{ fontSize: '11px', color: colors.textSecondary }}>
              {selectedLead.phone}
            </span>
          </>
        ) : (
          <>
            <Search size={14} color={colors.textTertiary} />
            <span style={{ fontSize: '13px', color: colors.textTertiary, flex: 1 }}>
              {loading ? 'Carregando leads...' : 'Selecionar um lead...'}
            </span>
          </>
        )}
        <ChevronDown size={14} color={colors.textTertiary} style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          marginTop: '4px', zIndex: 100,
          backgroundColor: colors.bgPrimary,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxHeight: '280px', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Search input */}
          <div style={{ padding: '8px', borderBottom: `1px solid ${colors.borderColor}` }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={14} style={{ position: 'absolute', left: '10px', color: colors.textTertiary, pointerEvents: 'none' }} />
              <input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome ou telefone..."
                autoFocus
                style={{
                  ...styles.input, width: '100%', padding: '8px 10px 8px 32px',
                  fontSize: '13px', border: `1px solid ${colors.borderColor}`,
                  borderRadius: '8px',
                }}
              />
            </div>
          </div>

          {/* Clear selection option */}
          {selectedLead && (
            <div
              onClick={() => { onSelect(null); setIsOpen(false); setSearchTerm(''); }}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: '12px',
                color: colors.textTertiary, borderBottom: `1px solid ${colors.borderColor}`,
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <RotateCcw size={12} /> Limpar seleção (usar número manual)
            </div>
          )}

          {/* Lead list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary, fontSize: '13px' }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '6px' }} />
                Carregando...
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: colors.textTertiary, fontSize: '13px' }}>
                Nenhum lead encontrado
              </div>
            ) : (
              filtered.slice(0, 50).map(lead => (
                <div
                  key={lead.id}
                  onClick={() => {
                    onSelect(lead);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  style={{
                    padding: '10px 12px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '10px',
                    backgroundColor: selectedLead?.id === lead.id ? `${colors.accentPrimary}10` : 'transparent',
                    borderBottom: `1px solid ${colors.borderColor}`,
                    transition: 'background-color 0.1s',
                  }}
                  onMouseEnter={e => {
                    if (selectedLead?.id !== lead.id) e.currentTarget.style.backgroundColor = colors.bgSecondary;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = selectedLead?.id === lead.id ? `${colors.accentPrimary}10` : 'transparent';
                  }}
                >
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%',
                    backgroundColor: `${colors.accentPrimary}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <User size={14} color={colors.accentPrimary} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px', fontWeight: 500, color: colors.textPrimary,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {lead.name || 'Sem nome'}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Phone size={10} /> {lead.phone || 'Sem telefone'}
                    </div>
                  </div>
                  {selectedLead?.id === lead.id && (
                    <Check size={14} color={colors.accentPrimary} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Main DetailsTab
// ============================================================
const DetailsTab = ({
  followup,
  history,
  colors,
  styles,
  testNumber,
  setTestNumber,
  sendingDay,
  onSendDay,
  onResetDay,
  onActivate,
  onUpdateFollowup,
  onOpenDayEditor,
  onAddDay,
  isBusinessChannel,
  approvedTemplates = [],
}) => {
  const [currentMonth, setCurrentMonth] = useState(1);
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
  const [testMode, setTestMode] = useState('manual'); // 'manual' | 'lead'
  const [selectedLead, setSelectedLead] = useState(null);
  const f = followup;
  const days = f.days || {};

  const allDays = Array.from({ length: f.total_days }, (_, i) => String(i + 1));

  const totalMonths = Math.max(1, Math.ceil(f.total_days / DAYS_PER_PAGE));
  const showMonthPagination = f.total_days > DAYS_PER_PAGE;

  const monthStart = (currentMonth - 1) * DAYS_PER_PAGE + 1;
  const monthEnd = Math.min(currentMonth * DAYS_PER_PAGE, f.total_days);

  const visibleDays = showMonthPagination
    ? allDays.filter(k => {
        const n = Number(k);
        return n >= monthStart && n <= monthEnd;
      })
    : allDays;

  // When a lead is selected, update testNumber with the lead's phone
  const handleLeadSelect = useCallback((lead) => {
    setSelectedLead(lead);
    if (lead) {
      setTestNumber(lead.phone || '');
    } else {
      setTestNumber('');
    }
  }, [setTestNumber]);

  // Check which days have been sent for the test number
  const sentDays = new Set();
  history.forEach(h => {
    if (h.status === 'sent' && h.recipient === testNumber.trim()) sentDays.add(h.day);
  });

  const handleAddDay = () => {
    if (f.total_days >= MAX_DAYS) return;
    const newDay = f.total_days + 1;
    const newMonth = Math.ceil(newDay / DAYS_PER_PAGE);
    setCurrentMonth(newMonth);
    onAddDay(newDay);
  };

  const canAddDay = f.total_days < MAX_DAYS;
  const addDayInCurrentPage = !showMonthPagination || currentMonth === totalMonths;

  const hasTestTarget = testNumber.trim().length > 0;

  return (
    <>
      {/* Summary Card */}
      <div style={{ ...styles.card, marginBottom: '20px', cursor: 'default' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ flex: 1, marginRight: '12px' }}>
            <InlineEditField
              value={f.name}
              onSave={(val) => onUpdateFollowup({ name: val })}
              placeholder="Nome do follow-up"
              colors={colors}
              textStyle={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}
              inputStyle={{ fontSize: '18px', fontWeight: 600 }}
            />
            <div style={{ marginTop: '4px' }}>
              <InlineEditField
                value={f.description}
                onSave={(val) => onUpdateFollowup({ description: val })}
                placeholder="Adicionar descricao..."
                multiline
                colors={colors}
                textStyle={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}
                inputStyle={{ fontSize: '13px' }}
              />
            </div>
          </div>
          <StatusBadge status={f.status} colors={colors} />
        </div>
        <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: colors.textSecondary }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Calendar size={14} /> {f.total_days} dias
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Users size={14} /> {(f.recipients || []).length} destinatarios
          </span>
        </div>
      </div>

      {/* Manual Test Panel */}
      <div style={{ ...styles.card, marginBottom: '20px', cursor: 'default' }}>
        <h4 style={{
          margin: '0 0 12px', fontSize: '15px', fontWeight: 600, color: colors.textPrimary,
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <Send size={16} color={colors.accentPrimary} /> Teste manual
        </h4>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', backgroundColor: colors.bgSecondary, borderRadius: '8px', padding: '3px' }}>
          <button
            onClick={() => { setTestMode('manual'); setSelectedLead(null); setTestNumber(''); }}
            style={{
              flex: 1, padding: '7px 12px', borderRadius: '6px', border: 'none',
              fontSize: '12px', fontWeight: 500, cursor: 'pointer',
              backgroundColor: testMode === 'manual' ? colors.bgPrimary : 'transparent',
              color: testMode === 'manual' ? colors.textPrimary : colors.textSecondary,
              boxShadow: testMode === 'manual' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            <Phone size={12} /> Número manual
          </button>
          <button
            onClick={() => { setTestMode('lead'); setTestNumber(''); setSelectedLead(null); }}
            style={{
              flex: 1, padding: '7px 12px', borderRadius: '6px', border: 'none',
              fontSize: '12px', fontWeight: 500, cursor: 'pointer',
              backgroundColor: testMode === 'lead' ? colors.bgPrimary : 'transparent',
              color: testMode === 'lead' ? colors.textPrimary : colors.textSecondary,
              boxShadow: testMode === 'lead' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            <User size={12} /> Selecionar lead
          </button>
        </div>

        {/* Input area */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
          {testMode === 'manual' ? (
            <input
              value={testNumber}
              onChange={e => setTestNumber(e.target.value)}
              placeholder="Número para teste (ex: 5511999998888)"
              style={{ ...styles.input, flex: 1 }}
            />
          ) : (
            <LeadSelector
              colors={colors}
              styles={styles}
              selectedLead={selectedLead}
              onSelect={handleLeadSelect}
            />
          )}
        </div>

        {/* Selected lead info badge */}
        {testMode === 'lead' && selectedLead && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', marginBottom: '12px', borderRadius: '8px',
            backgroundColor: `${colors.accentPrimary}08`,
            border: `1px solid ${colors.accentPrimary}30`,
          }}>
            <User size={14} color={colors.accentPrimary} />
            <span style={{ fontSize: '12px', color: colors.textPrimary, fontWeight: 500 }}>
              {selectedLead.name || 'Sem nome'}
            </span>
            <span style={{ fontSize: '11px', color: colors.textSecondary }}>•</span>
            <span style={{ fontSize: '12px', color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Phone size={10} /> {selectedLead.phone}
            </span>
            <span style={{ fontSize: '11px', color: colors.textSecondary, marginLeft: 'auto' }}>
              Envio para: <strong style={{ color: colors.textPrimary }}>{testNumber}</strong>
            </span>
          </div>
        )}

        {/* Month Pagination */}
        {showMonthPagination && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '12px',
          }}>
            <button
              onClick={() => setCurrentMonth(prev => Math.max(1, prev - 1))}
              disabled={currentMonth === 1}
              style={{
                ...styles.btn, ...styles.btnOutline,
                opacity: currentMonth === 1 ? 0.4 : 1,
                cursor: currentMonth === 1 ? 'default' : 'pointer',
                padding: '6px 12px', fontSize: '12px',
              }}
            >
              <ChevronLeft size={14} /> Anterior
            </button>
            <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
              Mes {currentMonth} de {totalMonths}
              <span style={{ fontWeight: 400, color: colors.textSecondary, marginLeft: '8px', fontSize: '12px' }}>
                (Dias {monthStart}-{monthEnd})
              </span>
            </span>
            <button
              onClick={() => setCurrentMonth(prev => Math.min(totalMonths, prev + 1))}
              disabled={currentMonth === totalMonths}
              style={{
                ...styles.btn, ...styles.btnOutline,
                opacity: currentMonth === totalMonths ? 0.4 : 1,
                cursor: currentMonth === totalMonths ? 'default' : 'pointer',
                padding: '6px 12px', fontSize: '12px',
              }}
            >
              Proximo <ChevronRight size={14} />
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
          {visibleDays.map(dayKey => {
            const dayNum = Number(dayKey);
            const isSent = sentDays.has(dayNum);
            const isSending = sendingDay === dayNum;
            const msgCount = (days[dayKey]?.messages || []).length;
            return (
              <div key={dayKey} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                padding: '10px', borderRadius: '10px',
                backgroundColor: isSent ? '#22c55e10' : msgCount > 0 ? colors.bgSecondary : `${colors.bgSecondary}80`,
                border: `1px solid ${isSent ? '#22c55e40' : colors.borderColor}`,
                position: 'relative',
              }}>
                {/* Config button */}
                <button
                  onClick={() => onOpenDayEditor(dayNum)}
                  style={{
                    position: 'absolute', top: '6px', right: '6px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: colors.textTertiary, padding: '2px',
                    borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'color 0.15s',
                  }}
                  title="Configurar mensagens"
                  onMouseEnter={e => e.currentTarget.style.color = colors.accentPrimary}
                  onMouseLeave={e => e.currentTarget.style.color = colors.textTertiary}
                >
                  <Settings size={14} />
                </button>

                <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                  Dia {dayNum}
                </span>
                <span style={{ fontSize: '11px', color: msgCount > 0 ? colors.textSecondary : colors.textTertiary, display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <MessageSquare size={10} /> {msgCount} msg{msgCount !== 1 ? 's' : ''}
                </span>
                {msgCount > 0 && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => onSendDay(dayNum)}
                      disabled={isSending || !hasTestTarget}
                      style={{
                        ...styles.btn, padding: '4px 10px', fontSize: '11px',
                        backgroundColor: isSent ? '#22c55e' : colors.accentPrimary,
                        color: 'white', opacity: isSending || !hasTestTarget ? 0.5 : 1,
                      }}
                    >
                      {isSending ? <Loader2 size={12} /> : isSent ? <Check size={12} /> : <Send size={12} />}
                      {isSending ? '...' : isSent ? 'Enviado' : 'Enviar'}
                    </button>
                    {isSent && (
                      <button
                        onClick={() => onResetDay(dayNum)}
                        style={{ ...styles.btn, ...styles.btnOutline, padding: '4px 8px', fontSize: '11px' }}
                        title="Resetar para reenviar"
                      >
                        <RotateCcw size={12} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add New Day Card */}
          {canAddDay && addDayInCurrentPage && (
            <div
              onClick={handleAddDay}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '8px', padding: '10px', borderRadius: '10px',
                border: `2px dashed ${colors.accentPrimary}40`,
                backgroundColor: 'transparent',
                cursor: 'pointer', minHeight: '90px',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = colors.accentPrimary;
                e.currentTarget.style.backgroundColor = `${colors.accentPrimary}08`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = `${colors.accentPrimary}40`;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Plus size={20} color={colors.accentPrimary} />
              <span style={{ fontSize: '12px', fontWeight: 500, color: colors.accentPrimary }}>
                Dia {f.total_days + 1}
              </span>
              <span style={{ fontSize: '10px', color: colors.textTertiary }}>
                {f.total_days}/{MAX_DAYS}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Activation Panel */}
      {(f.status === 'draft' || f.status === 'paused') && (
        <div style={{ ...styles.card, marginBottom: '20px', cursor: 'default' }}>
          <h4 style={{
            margin: '0 0 12px', fontSize: '15px', fontWeight: 600, color: colors.textPrimary,
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <Play size={16} color={colors.accentPrimary} /> Ativar follow-up
          </h4>

          <button onClick={() => onActivate()} style={{
            ...styles.btn, ...styles.btnPrimary, padding: '10px 20px', fontSize: '14px',
          }}>
            <Play size={16} /> Ativar
          </button>
          <p style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '8px' }}>
            Cada destinatario comeca do dia 1 a partir do momento em que e adicionado.
          </p>
        </div>
      )}

      {/* Advanced Config (WhatsApp Business only) */}
      {isBusinessChannel && (
        <div style={{ ...styles.card, cursor: 'default' }}>
          <button
            onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', background: 'none', border: 'none', cursor: 'pointer',
              padding: 0,
            }}
          >
            <h4 style={{
              margin: 0, fontSize: '15px', fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <Settings size={16} color={colors.accentPrimary} /> Configuracoes avancadas
            </h4>
            {showAdvancedConfig ? (
              <ChevronUp size={18} color={colors.textSecondary} />
            ) : (
              <ChevronDown size={18} color={colors.textSecondary} />
            )}
          </button>

          {showAdvancedConfig && (
            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                  Template de Fallback (janela 24h fechada)
                </label>
                <select
                  value={f.business_config?.fallback_template_name || ''}
                  onChange={e => onUpdateFollowup({
                    business_config: { ...f.business_config, fallback_template_name: e.target.value }
                  })}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: '14px', outline: 'none',
                  }}
                >
                  <option value="">Nenhum (nao enviar fallback)</option>
                  {approvedTemplates.map(t => (
                    <option key={t.name} value={t.name}>{t.name} ({t.language})</option>
                  ))}
                </select>
                <p style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  Quando o conteudo custom falhar por janela de 24h, este template sera enviado como alternativa.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                    Intervalo de retentativa (horas)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={168}
                    value={f.business_config?.retry_interval_hours || 6}
                    onChange={e => onUpdateFollowup({
                      business_config: { ...f.business_config, retry_interval_hours: parseInt(e.target.value) || 6 }
                    })}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '8px',
                      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                      color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                    Maximo de tentativas
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={f.business_config?.max_retries || 3}
                    onChange={e => onUpdateFollowup({
                      business_config: { ...f.business_config, max_retries: parseInt(e.target.value) || 3 }
                    })}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '8px',
                      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                      color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <p style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: '1.5', margin: 0 }}>
                Se o envio do conteudo falhar por janela de 24h, o sistema tentara novamente a cada{' '}
                <strong>{f.business_config?.retry_interval_hours || 6}h</strong>, ate{' '}
                <strong>{f.business_config?.max_retries || 3}x</strong>. Apos esgotar, o lead sera marcado como "nao respondeu".
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DetailsTab;
